import type { SearchSchemaInput } from '@tanstack/react-router';

/**
 * عقد `search params` لشاشة المشروع — **معزول في ملف بلا مكوّنات، وعزله قرار
 * ميزانية لا ترتيب ملفات** (نفس علّة `features/settings/search.ts` المقيسة:
 * `validateSearch` خيار مسار يبقى في الحزمة الابتدائية، ووحدة التقسيم في Rollup
 * هي الملف — فلو سكن هذا المدقّق `project-detail.tsx` لسحب الشاشة كلها ومعها
 * `motion` إلى المدخل. القاعدة الملزمة: لا خيار مسار يسكن ملفًا فيه مكوّن).
 *
 * الوجه يعيش في search params (§1.2): الرابط قابل للمشاركة («افتح مهارات مشروع
 * X»)، والرجوع بالتاريخ يحفظ الوجه المفتوح بدل أن ينهار إلى الافتراضي.
 *
 * الأوجه بقرار المالك الحرفي (2026-07-30): «كل مشروع يكون فيه: نقاط التقدم،
 * الأشياء المنجزة واللي تنتظر الإنجاز، مهاراته الخاصة فيه، شخصياته الخاصة فيه،
 * تنبيهاته الخاصة فيه» — واليومية القائمة تبقى الوجه الافتراضي لأنها إجابة
 * «وين وصلنا؟»، والذاكرة أضيفت لاحقًا كوجهٍ دائم للمحادثات والمصادر.
 */

export const PROJECT_FACETS = [
  /* اللوحة أولًا (د2): وجهٌ يركّب البقية بترتيب المالك — فمن رتّبها يريدها أولَ ما يرى. */
  { id: 'dashboard', label: 'اللوحة' },
  { id: 'journal', label: 'اليومية' },
  /* الأقسام (المحاور) — أول ظهور للمحور في الويب، المرحلة ب1. موضعه بعد اليومية
     مباشرةً لأنه بنية المشروع لا تفصيلًا فيه: من يقسّم مشروعه يفعلها مبكرًا. */
  { id: 'sections', label: 'الأقسام' },
  { id: 'milestones', label: 'نقاط التقدم' },
  { id: 'tasks', label: 'المهام' },
  { id: 'skills', label: 'المهارات' },
  { id: 'personas', label: 'الشخصيات' },
  { id: 'memory', label: 'الذاكرة' },
  { id: 'reminders', label: 'التنبيهات' },
] as const;

export type ProjectFacetId = (typeof PROJECT_FACETS)[number]['id'];

export const FACET_IDS: readonly ProjectFacetId[] = PROJECT_FACETS.map((facet) => facet.id);

export const DEFAULT_PROJECT_FACET: ProjectFacetId = 'journal';

export interface ProjectDetailSearch {
  readonly facet: ProjectFacetId;
}

/** الدخل بعلامة الموجّه: كل حقوله اختيارية فتبقى روابط `to="/projects/$projectId"` القائمة بلا search. */
export type ProjectDetailSearchInput = { readonly facet?: unknown } & SearchSchemaInput;

/** يتسامح مع أي قيمة في الـ URL: وجه مجهول يعود إلى اليومية بلا شاشة خطأ. */
export function validateProjectDetailSearch(input: ProjectDetailSearchInput): ProjectDetailSearch {
  return { facet: FACET_IDS.find((id) => id === input.facet) ?? DEFAULT_PROJECT_FACET };
}
